import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowUpRight, BarChart3 } from "lucide-react";

function StatusChip({ status }) {
  const m = {
    scheduled: { c: "border-white/20 text-white/60", l: "Geplant" },
    live: { c: "border-red-400/40 text-red-400", l: "Live" },
    finished: { c: "border-accent/40 text-accent", l: "Beendet" },
  }[status] || { c: "border-white/20 text-white/60", l: status };
  return (
    <span className={`chip ${m.c} backdrop-blur-md`}>
      {status === "live" && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
      {m.l}
    </span>
  );
}

export default function LiveMatches() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const params = filter === "all" ? {} : { status: filter };
    const { data } = await api.get("/matches", { params });
    setItems(data);
  }, [filter]);

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [load]);

  const filters = [
    { k: "all", l: "Alle" },
    { k: "live", l: "Live" },
    { k: "scheduled", l: "Geplant" },
    { k: "finished", l: "Beendet" },
  ];

  return (
    <div className="space-y-8 fade-in" data-testid="live-matches-page">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient">Live Matches</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">EA FC 26 · Echtzeit</h1>
        <p className="text-white/60 text-sm mt-2">Alle laufenden, geplanten und vergangenen Matches im Verein.</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`live-filter-${f.k}`}
            className={`px-4 py-2 rounded-sm text-xs uppercase tracking-[0.2em] font-mono border transition-colors ${
              filter === f.k ? "bg-primary/10 border-primary text-primary" : "border-border text-white/60 hover:border-primary/40"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-white/10 p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-white/40">
          Keine Matches in dieser Kategorie.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((m) => (
            <Link
              key={m.id}
              to={`/live/${m.id}`}
              data-testid={`match-card-${m.id}`}
              className="card-solid p-5 hover:border-primary/40 transition-colors group"
            >
              <div className="flex items-center justify-between mb-4">
                <StatusChip status={m.status} />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                  {m.status === "live" ? `${m.minute}'` : new Date(m.start_time).toLocaleString("de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg tracking-tight truncate">{m.home_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{m.home_team}</div>
                </div>
                <div className="font-display text-3xl tabular-nums text-gradient">
                  {m.home_score}<span className="text-white/30 mx-2">·</span>{m.away_score}
                </div>
                <div className="flex-1 min-w-0 text-right">
                  <div className="font-display text-lg tracking-tight truncate">{m.away_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{m.away_team}</div>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-white/50 font-mono">
                <span>{m.events?.length || 0} Events</span>
                <ArrowUpRight className="w-3 h-3 group-hover:text-primary" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function LiveMatchDetail() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${id}`);
      setM(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [load]);

  if (error) return <div className="text-red-400 text-sm font-mono">{error}</div>;
  if (!m) return <div className="font-mono text-xs text-white/40">Loading…</div>;

  const events = (m.events || []).slice().sort((a, b) => a.minute - b.minute);

  return (
    <div className="space-y-6 fade-in" data-testid="match-detail-page">
      <Link to="/live" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-primary link-underline">← Live Matches</Link>

      <div className="card-solid p-6 tracing-border">
        <div className="flex items-center justify-between mb-6">
          <StatusChip status={m.status} />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            {m.status === "live" ? `Minute ${m.minute}'` : new Date(m.start_time).toLocaleString("de-DE")}
          </span>
        </div>
        <div className="grid grid-cols-3 items-center gap-4 mb-6">
          <div>
            <div className="font-display text-2xl tracking-tight">{m.home_name}</div>
            <div className="font-mono text-xs text-white/50 mt-1">{m.home_team}</div>
          </div>
          <div className="text-center">
            <div className="font-display text-6xl tabular-nums text-gradient">{m.home_score} · {m.away_score}</div>
          </div>
          <div className="text-right">
            <div className="font-display text-2xl tracking-tight">{m.away_name}</div>
            <div className="font-mono text-xs text-white/50 mt-1">{m.away_team}</div>
          </div>
        </div>

        <Link to={`/matches/${m.id}/analysis`} className="btn-secondary" data-testid="match-analysis-link">
          <BarChart3 className="w-4 h-4" /> Match-Analyse öffnen
        </Link>
      </div>

      <div className="card-solid p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-4">Timeline</div>
        {events.length === 0 ? (
          <div className="text-white/40 font-mono text-xs uppercase tracking-[0.2em] py-6 text-center">
            Noch keine Events.
          </div>
        ) : (
          <div className="space-y-2">
            {events.map((e) => (
              <div
                key={e.id}
                data-testid={`event-${e.id}`}
                className={`flex items-center gap-3 p-3 border-l-2 ${
                  e.team === "home" ? "border-primary" : e.team === "away" ? "border-magenta" : "border-white/30"
                } bg-white/5`}
              >
                <div className="font-mono tabular-nums text-sm w-12">{e.minute}'</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 w-16">{e.type}</div>
                <div className="flex-1">
                  <div className="text-sm">{e.player || "—"}</div>
                  {e.detail && <div className="text-xs text-white/50">{e.detail}</div>}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{e.team}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
